import { createHttpService } from '../ApiService';
import { normalizePaginatedResponse } from '../__shared/paginationUtils';
import { mockFinanceList } from '../../__mocks__/finance/mockFinanceList';
import { mockFinancialGrantList } from '../../__mocks__/finance/mockFinancialGrantList';
import {
  TFinance,
  TFinanceApiResponse,
  TFinanceDetails,
  TFinancePrimary,
  TFinanceRecordData,
  FinanceListParams,
  TFinanceCreateRequest,
  TFinanceUpdateRequest,
} from './finance.interfaces';
import {
  TFinancialGrant,
  TFinancialGrantResponse,
  GrantListParams,
  TGrantCreateRequest,
  TGrantUpdateRequest,
} from './financialGrants.interfaces';
import { mockFinanceDetails } from 'src/__mocks__/finance/financeDetailsMock';
import { useRbacState } from '../../context/rbac.context';
import { checkApiPermission } from '../../utils/rbacHelpers';
import isAppWithMock from 'src/utils/isAppWithMock';

export const useFinanceService = () => {
  const httpAPI = createHttpService();
  const baseFinancePath = '/finance';
  const isMockData = isAppWithMock();
  const { rbac } = useRbacState();

  // ===================== FINANCE TRANSACTIONS =====================

  /**
   * Get paginated list of finance transactions with filtering support
   */
  const getFinanceList = (
    params: FinanceListParams
  ): Promise<TFinanceApiResponse> => {
    if (isMockData) {
      // Apply client-side filtering for mock data
      let filteredData = [...mockFinanceList];

      if (params.type && params.type !== 'all') {
        filteredData = filteredData.filter((item) => item.type === params.type);
      }

      if (params.status) {
        filteredData = filteredData.filter(
          (item) => item.status === params.status
        );
      }

      if (params.category) {
        filteredData = filteredData.filter(
          (item) => item.category === params.category
        );
      }

      if (params.paymentMode) {
        filteredData = filteredData.filter(
          (item) => item.paymentMode === params.paymentMode
        );
      }

      if (params.academicYear) {
        filteredData = filteredData.filter(
          (item) => item.academicYear === params.academicYear
        );
      }

      if (params.search) {
        const searchLower = params.search.toLowerCase();
        filteredData = filteredData.filter(
          (item) =>
            item.name.toLowerCase().includes(searchLower) ||
            item.description.toLowerCase().includes(searchLower)
        );
      }

      // Pagination
      const page = params.page || 1;
      const limit = params.limit || 10;
      const startIndex = (page - 1) * limit;
      const paginatedData = filteredData.slice(startIndex, startIndex + limit);
      const totalPages = Math.ceil(filteredData.length / limit);

      return Promise.resolve({
        data: paginatedData,
        total: filteredData.length,
        page,
        limit,
        totalPages,
      });
    }

    // Real API call with filter params
    const requestParams: Record<string, any> = {
      page: params.page || 1,
      limit: params.limit || 10,
    };

    // Add optional filter params
    if (params.type && params.type !== 'all') requestParams.type = params.type;
    if (params.status) requestParams.status = params.status;
    if (params.category) requestParams.category = params.category;
    if (params.paymentMode) requestParams.paymentMode = params.paymentMode;
    if (params.academicYear) requestParams.academicYear = params.academicYear;
    if (params.startDate) requestParams.startDate = params.startDate;
    if (params.endDate) requestParams.endDate = params.endDate;
    if (params.search) requestParams.search = params.search;
    if (params.sortBy) requestParams.sortBy = params.sortBy;
    if (params.sortOrder) requestParams.sortOrder = params.sortOrder;

    return httpAPI
      .get<Record<string, unknown>>(`${baseFinancePath}`, {
        params: requestParams,
      })
      .then((res) =>
        normalizePaginatedResponse<TFinancePrimary>(res as { data?: TFinancePrimary[]; [k: string]: unknown }, 'data')
      )
      .catch((error) => {
        console.error('Error fetching finance list:', error);
        return normalizePaginatedResponse<TFinancePrimary>({
          data: mockFinanceList,
          count: mockFinanceList.length,
          page: 1,
          limit: 10,
        });
      });
  };

  /**
   * Get finance transaction details by ID
   */
  const getFinanceById = (financeId: string): Promise<TFinanceDetails> => {
    if (isMockData) {
      return Promise.resolve(mockFinanceDetails);
    }
    return httpAPI
      .get<TFinanceDetails>(`${baseFinancePath}/${financeId}`)
      .catch((error) => {
        console.error('Error fetching finance details:', error);
        throw error;
      });
  };

  /**
   * Create a new finance transaction
   */
  const createFinance = (
    data: TFinanceCreateRequest
  ): Promise<TFinance> => {
    checkApiPermission(rbac, 'finance', 'financeList', 'write');

    if (isMockData) {
      const newFinance: TFinance = {
        id: `fin_${Date.now()}`,
        amount: data.amount,
        currencyCode: data.currencyCode,
        description: data.description,
        fine: data.fine || 0,
        fineCurrencyCode: data.fineCurrencyCode || data.currencyCode,
        instituteId: data.instituteId,
        isCredit: data.isCredit,
        tags: data.tags,
        type: data.type as string,
        category: data.category,
        counterRef: data.counterRef,
        counterRefType: data.counterRefType,
        academicYear: data.academicYear,
        dueDate: new Date(data.dueDate),
        purpose: data.purpose,
        invoiceKey: data.invoiceKey || `inv_${Date.now()}`,
        createdBy: {
          id: 'mock-user',
          name: 'Mock User',
          userRole: 'admin',
          createdAt: new Date(),
        },
        createdAt: new Date(),
      };
      return Promise.resolve(newFinance);
    }

    return httpAPI
      .post<TFinance>(`${baseFinancePath}/create`, data)
      .catch((error) => {
        console.error('Error creating finance record:', error);
        throw error;
      });
  };

  /**
   * Update an existing finance transaction
   */
  const updateFinance = (
    data: TFinanceUpdateRequest
  ): Promise<TFinance> => {
    checkApiPermission(rbac, 'finance', 'financeList', 'write');

    if (isMockData) {
      const updatedFinance: TFinance = {
        id: data.id,
        amount: data.amount,
        currencyCode: data.currencyCode,
        description: data.description,
        fine: data.fine || 0,
        fineCurrencyCode: data.fineCurrencyCode || data.currencyCode,
        instituteId: data.instituteId,
        isCredit: data.isCredit,
        tags: data.tags,
        type: data.type as string,
        category: data.category,
        counterRef: data.counterRef,
        counterRefType: data.counterRefType,
        academicYear: data.academicYear,
        dueDate: new Date(data.dueDate),
        purpose: data.purpose,
        invoiceKey: data.invoiceKey || '',
        createdBy: {
          id: 'mock-user',
          name: 'Mock User',
          userRole: 'admin',
          createdAt: new Date(),
        },
        createdAt: new Date(),
      };
      return Promise.resolve(updatedFinance);
    }

    return httpAPI
      .put<TFinance>(`${baseFinancePath}/update`, data)
      .catch((error) => {
        console.error('Error updating finance record:', error);
        throw error;
      });
  };

  /**
   * Delete a finance transaction
   */
  const deleteFinance = (financeId: string): Promise<void> => {
    checkApiPermission(rbac, 'finance', 'financeList', 'delete');

    if (isMockData) {
      return Promise.resolve();
    }

    return httpAPI
      .delete<void>(`${baseFinancePath}/delete/${financeId}`)
      .catch((error) => {
        console.error('Error deleting finance record:', error);
        throw error;
      });
  };

  // ===================== FINANCIAL GRANTS =====================

  /**
   * Get paginated list of financial grants with filtering support
   */
  const getGrantList = (
    params: GrantListParams
  ): Promise<TFinancialGrantResponse> => {
    if (isMockData) {
      // Apply client-side filtering for mock data
      let filteredData = [...mockFinancialGrantList];

      if (params.grantType) {
        filteredData = filteredData.filter(
          (item) => item.grantType === params.grantType
        );
      }

      if (params.status && params.status !== 'all') {
        filteredData = filteredData.filter(
          (item) => item.status === params.status
        );
      }

      if (params.applicationStatus) {
        filteredData = filteredData.filter(
          (item) => item.applicationStatus === params.applicationStatus
        );
      }

      if (params.providerType) {
        filteredData = filteredData.filter(
          (item) => item.providerType === params.providerType
        );
      }

      if (params.disbursementFrequency) {
        filteredData = filteredData.filter(
          (item) => item.disbursementFrequency === params.disbursementFrequency
        );
      }

      if (params.minAmount) {
        filteredData = filteredData.filter(
          (item) => item.amount >= params.minAmount!
        );
      }

      if (params.maxAmount) {
        filteredData = filteredData.filter(
          (item) => item.amount <= params.maxAmount!
        );
      }

      if (params.currency) {
        filteredData = filteredData.filter(
          (item) => item.currency === params.currency
        );
      }

      if (params.search) {
        const searchLower = params.search.toLowerCase();
        filteredData = filteredData.filter(
          (item) =>
            item.name.toLowerCase().includes(searchLower) ||
            item.description.toLowerCase().includes(searchLower) ||
            item.providerName.toLowerCase().includes(searchLower)
        );
      }

      // Pagination
      const page = params.page || 1;
      const limit = params.limit || 10;
      const startIndex = (page - 1) * limit;
      const paginatedData = filteredData.slice(startIndex, startIndex + limit);

      return Promise.resolve({
        data: paginatedData,
        total: filteredData.length,
        page,
        size: limit,
      });
    }

    // Real API call with filter params
    const requestParams: Record<string, any> = {
      page: params.page || 1,
      limit: params.limit || 10,
    };

    // Add optional filter params
    if (params.grantType) requestParams.grantType = params.grantType;
    if (params.status && params.status !== 'all')
      requestParams.status = params.status;
    if (params.applicationStatus)
      requestParams.applicationStatus = params.applicationStatus;
    if (params.providerType) requestParams.providerType = params.providerType;
    if (params.disbursementFrequency)
      requestParams.disbursementFrequency = params.disbursementFrequency;
    if (params.minAmount) requestParams.minAmount = params.minAmount;
    if (params.maxAmount) requestParams.maxAmount = params.maxAmount;
    if (params.currency) requestParams.currency = params.currency;
    if (params.search) requestParams.search = params.search;
    if (params.sortBy) requestParams.sortBy = params.sortBy;
    if (params.sortOrder) requestParams.sortOrder = params.sortOrder;

    return httpAPI
      .get<TFinancialGrantResponse>(`${baseFinancePath}/get-grants`, {
        params: requestParams,
      })
      .catch((error) => {
        console.error('Error fetching grants list:', error);
        // Fallback to mock data on error
        return {
          data: mockFinancialGrantList,
          total: mockFinancialGrantList.length,
          page: 1,
          size: 10,
        };
      });
  };

  /**
   * Get financial grant by ID
   */
  const getGrantById = (grantId: string): Promise<TFinancialGrant> => {
    if (isMockData) {
      const grant = mockFinancialGrantList.find((g) => g.id === grantId);
      if (grant) {
        return Promise.resolve(grant);
      }
      return Promise.reject(new Error('Grant not found'));
    }

    return httpAPI
      .get<TFinancialGrant>(`${baseFinancePath}/get-grant/${grantId}`)
      .catch((error) => {
        console.error('Error fetching grant details:', error);
        throw error;
      });
  };

  /**
   * Create a new financial grant
   */
  const createGrant = (
    data: TGrantCreateRequest
  ): Promise<TFinancialGrant> => {
    checkApiPermission(rbac, 'finance', 'scholarships', 'write');

    if (isMockData) {
      const newGrant: TFinancialGrant = {
        id: `grant_${Date.now()}`,
        ...data,
        currency: data.currency as any,
        disbursementFrequency: data.disbursementFrequency as any,
        grantType: data.grantType as any,
        providerType: data.providerType as any,
        status: data.status as any,
        applicationStatus: data.applicationStatus as any,
        documents: [],
        actions: {
          created: {
            userId: 'mock-user',
            timestamp: Date.now(),
            name: 'Mock User',
          },
        },
      };
      return Promise.resolve(newGrant);
    }

    return httpAPI
      .post<TFinancialGrant>(`${baseFinancePath}/create-grant`, data)
      .catch((error) => {
        console.error('Error creating grant:', error);
        throw error;
      });
  };

  /**
   * Update an existing financial grant
   */
  const updateGrant = (
    data: TGrantUpdateRequest
  ): Promise<TFinancialGrant> => {
    checkApiPermission(rbac, 'finance', 'scholarships', 'write');

    if (isMockData) {
      const { id, ...restData } = data;
      const updatedGrant: TFinancialGrant = {
        ...restData,
        id,
        currency: data.currency as any,
        disbursementFrequency: data.disbursementFrequency as any,
        grantType: data.grantType as any,
        providerType: data.providerType as any,
        status: data.status as any,
        applicationStatus: data.applicationStatus as any,
        documents: [],
        actions: {
          created: {
            userId: 'mock-user',
            timestamp: Date.now(),
            name: 'Mock User',
          },
          updated: {
            userId: 'mock-user',
            timestamp: Date.now(),
            name: 'Mock User',
          },
        },
      };
      return Promise.resolve(updatedGrant);
    }

    return httpAPI
      .put<TFinancialGrant>(`${baseFinancePath}/update-grant`, data)
      .catch((error) => {
        console.error('Error updating grant:', error);
        throw error;
      });
  };

  /**
   * Delete a financial grant
   */
  const deleteGrant = (grantId: string): Promise<void> => {
    checkApiPermission(rbac, 'finance', 'scholarships', 'delete');

    if (isMockData) {
      return Promise.resolve();
    }

    return httpAPI
      .delete<void>(`${baseFinancePath}/delete-grant/${grantId}`)
      .catch((error) => {
        console.error('Error deleting grant:', error);
        throw error;
      });
  };

  // ===================== LEGACY METHODS (for backward compatibility) =====================

  const getFinancialGrantList = getGrantList;
  const getFinanceRecordById = getFinanceById;
  const createFinanceRecord = (financeData: TFinance): Promise<TFinance> => {
    return createFinance(financeData as unknown as TFinanceCreateRequest);
  };
  const patchFinanceRecord = (financeData: TFinance): Promise<TFinance> => {
    return updateFinance(financeData as unknown as TFinanceUpdateRequest);
  };
  const deleteFinanceRecord = deleteFinance;
  const creatFinanceGrant = (data: TFinancialGrant): Promise<TFinancialGrant> => {
    return createGrant(data as unknown as TGrantCreateRequest);
  };
  const patchFinanceGrant = (data: TFinancialGrant): Promise<TFinancialGrant> => {
    return updateGrant(data as unknown as TGrantUpdateRequest);
  };
  const deleteFinanceGrant = deleteGrant;
  const getFinanceGrantById = getGrantById;
  const getFinanceGrantList = getGrantList;
  const getFinanceRecords = (params: any): Promise<TFinanceRecordData[]> => {
    return httpAPI.get<TFinanceRecordData[]>(`${baseFinancePath}`, { params });
  };

  return {
    // New API methods
    getFinanceList,
    getFinanceById,
    createFinance,
    updateFinance,
    deleteFinance,
    getGrantList,
    getGrantById,
    createGrant,
    updateGrant,
    deleteGrant,
    // Legacy methods for backward compatibility
    getFinancialGrantList,
    getFinanceRecords,
    getFinanceRecordById,
    createFinanceRecord,
    patchFinanceRecord,
    deleteFinanceRecord,
    creatFinanceGrant,
    patchFinanceGrant,
    deleteFinanceGrant,
    getFinanceGrantById,
    getFinanceGrantList,
  };
};
